import React, { createContext, useState, useContext, useCallback } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { getApiBaseUrl } from '../config';

/**
 * Managed player backgrounds (DJB-224) and the one background a machine is
 * subscribed to (DJB-225).
 *
 * @typedef {Object} Background
 * @property {string} id
 * @property {string} name
 * @property {string} uploadedAt           ISO date-time
 * @property {string[]} subscribedMachineIds
 */

export const S3_PUBLIC_BASE_URL = 'https://tdj-s3.s3.eu-west-2.amazonaws.com/';
export const BACKGROUND_NAME_MAX_LENGTH = 256;
export const BACKGROUND_FILE_ERROR = 'The background must be a JPG image.';

/** The image of a background is stored under backgrounds/<id>.jpg; the id never changes, so no cache buster. */
export const backgroundUrl = (id) => `${S3_PUBLIC_BASE_URL}backgrounds/${id}.jpg`;

/** JPG by MIME type, or by extension whenever the MIME type isn't image/jpeg. */
export const isJpegFile = (file) => {
  if (!file) return false;
  if (file.type === 'image/jpeg') return true;
  return /\.jpe?g$/i.test(file.name ?? '');
};

const normalize = (bg) => ({
  ...bg,
  subscribedMachineIds: Array.isArray(bg?.subscribedMachineIds) ? bg.subscribedMachineIds : [],
});

const messageOf = async (response, fallback) => {
  try {
    const body = await response.json();
    return body?.message || fallback;
  } catch {
    return fallback;
  }
};

export const BackgroundContext = createContext({
  /** @type {Background[]} */
  backgrounds: [],
  loading: false,
  error: null,
  fetchBackgrounds: async () => [],
  createBackground: async () => null,
  renameBackground: async () => null,
  deleteBackground: async () => false,
  assignBackground: async () => false,
  unassignBackground: async () => false,
});

// The list is fetched lazily by the sections that need it (Media Studio's
// Background Editor and the Background subscriptions section), not on app start.
const BackgroundProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now() + Math.random();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };
  const showErrorNotification = (message) => addNotification(message, 'error');
  const showSuccessNotification = (message) => addNotification(message, 'success');

  const accessToken = authData?.accessToken;
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);
  const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

  /** GET /backgrounds — resolves the fresh list (also stored in context). */
  const fetchBackgrounds = useCallback(async () => {
    if (!accessToken) return [];
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${getApiBaseUrl()}/backgrounds`, { headers: authHeaders() });
      if (!response.ok) throw new Error(`Failed to load backgrounds (${response.status})`);
      const list = (await response.json()).map(normalize);
      setBackgrounds(list);
      return list;
    } catch (err) {
      setError(err.message);
      setBackgrounds([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [accessToken, authHeaders]);

  /** POST /backgrounds (multipart file + name) — resolves the created background or null. */
  const createBackground = async (file, name) => {
    if (!accessToken) return null;
    if (!isJpegFile(file)) {
      showErrorNotification(BACKGROUND_FILE_ERROR);
      return null;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (name && name.trim()) formData.append('name', name.trim());
      const response = await fetch(`${getApiBaseUrl()}/backgrounds`, { method: 'POST', headers: authHeaders(), body: formData });
      if (!response.ok) throw new Error(await messageOf(response, 'Failed to upload the background'));
      const created = normalize(await response.json());
      setBackgrounds((prev) => [created, ...prev]);
      showSuccessNotification(`Background “${created.name}” uploaded`);
      return created;
    } catch (err) {
      showErrorNotification(err.message);
      return null;
    }
  };

  /** PUT /backgrounds/{id} {name} — resolves the updated background or null. */
  const renameBackground = async (id, name) => {
    if (!accessToken || !id) return null;
    try {
      const response = await fetch(`${getApiBaseUrl()}/backgrounds/${id}`, {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error(await messageOf(response, 'Failed to rename the background'));
      const updated = normalize(await response.json());
      setBackgrounds((prev) => prev.map((bg) => (bg.id === id ? { ...bg, ...updated } : bg)));
      return updated;
    } catch (err) {
      showErrorNotification(err.message);
      return null;
    }
  };

  /** DELETE /backgrounds/{id} — subscribed machines fall back to their default background. */
  const deleteBackground = async (id) => {
    if (!accessToken || !id) return false;
    try {
      const response = await fetch(`${getApiBaseUrl()}/backgrounds/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error(await messageOf(response, 'Failed to delete the background'));
      setBackgrounds((prev) => prev.filter((bg) => bg.id !== id));
      showSuccessNotification('Background deleted');
      return true;
    } catch (err) {
      showErrorNotification(err.message);
      return false;
    }
  };

  const moveMachine = (machineId, targetBackgroundId) =>
    setBackgrounds((prev) =>
      prev.map((bg) => {
        const without = bg.subscribedMachineIds.filter((m) => m !== machineId);
        return bg.id === targetBackgroundId
          ? { ...bg, subscribedMachineIds: [...without, machineId] }
          : { ...bg, subscribedMachineIds: without };
      })
    );

  /** PUT /machines/{machineId}/background {backgroundId} — replaces the machine's subscription. */
  const assignBackground = async (machineId, backgroundId) => {
    if (!accessToken || !machineId || !backgroundId) return false;
    try {
      const response = await fetch(`${getApiBaseUrl()}/machines/${machineId}/background`, {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ backgroundId }),
      });
      if (!response.ok) throw new Error(await messageOf(response, 'Failed to assign the background'));
      moveMachine(machineId, backgroundId);
      return true;
    } catch (err) {
      showErrorNotification(err.message);
      return false;
    }
  };

  /** DELETE /machines/{machineId}/background — 202 (removed) and 204 (was not subscribed) both succeed. */
  const unassignBackground = async (machineId) => {
    if (!accessToken || !machineId) return false;
    try {
      const response = await fetch(`${getApiBaseUrl()}/machines/${machineId}/background`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error(await messageOf(response, 'Failed to remove the background from the machine'));
      moveMachine(machineId, null);
      return true;
    } catch (err) {
      showErrorNotification(err.message);
      return false;
    }
  };

  return (
    <BackgroundContext.Provider
      value={{ backgrounds, loading, error, fetchBackgrounds, createBackground, renameBackground, deleteBackground, assignBackground, unassignBackground }}
    >
      {children}
      {notifications.map((n) => (
        <Notification
          key={n.id}
          message={n.message}
          type={n.type}
          onClose={() => setNotifications((prev) => prev.filter((x) => x.id !== n.id))}
        />
      ))}
    </BackgroundContext.Provider>
  );
};

export default BackgroundProvider;
